import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import useInvalidatedPatientQueries from 'components/patient/useInvalidatedPatientQueries';
import { proposedStatusMap } from 'components/referral/ReferralMenu';
import { StatusChip } from 'components/referrals/referralsTableColumns';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal from 'components/shared/StandardModal';
import { useEpisode } from 'context/episode';
import { useProfile } from 'context/profile';
import useMutateWithToast from 'hooks/useMutateWithToast';
import LocationEpisode from 'models/LocationEpisode';
import Referral from 'models/reviews/Referral';
import { updateReview } from 'services/api/reviews';
import colors from 'styles/theme/colors';
import RightArrow from 'svg/RightArrow';

type StatusUpdateModalProps = {
  setShow: (val: boolean) => void;
  locationEpisode: LocationEpisode;
  referral: Referral;
};

const StatusUpdateModal = (props: StatusUpdateModalProps) => {
  const { setShow, locationEpisode, referral } = props;

  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [denialReason, setDenialReason] = useState<{ apiName: string; displayName: string }>();

  const { note, setNote } = useActivityInput();
  const { profile } = useProfile();
  const invalidatePatientQueries = useInvalidatedPatientQueries();
  const navigate = useNavigate();
  const { refetch } = useEpisode();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: updateReview,
    onSuccess: async () => {
      try {
        await refetch({ throwOnError: true });

        invalidatePatientQueries({
          episode: { id: locationEpisode.episodeId },
          locationEpisode: { id: locationEpisode.id },
          referrals: true,
          referral: { id: referral.id },
          attachmentsCount: true,
        });
      } catch {
        navigate(`/referrals/${referral.id}`);
      }

      setShow(false);
    },
    onSettled: () => {
      setAttachmentUploading(false);
    },
  });

  const handleSubmit = () => {
    delete note?.escalationType;

    mutate({
      id: referral.id,
      status: proposedStatusMap[referral.status].status,
      denialReasonApiName: denialReason?.apiName,
      reviewedById: profile?.id,
      note: note
        ? {
            ...note,
            attachments: note.attachments?.map((upload) => upload.serialize()),
          }
        : undefined,
    });
  };

  const submitDisabled = attachmentUploading || isPending || (referral.accepted && !denialReason);

  return (
    <StandardModal title='Change Referral Status' onCancel={() => setShow(false)}>
      <StyledContainer>
        <PatientName>Patient: {locationEpisode.patient.name}</PatientName>
        <StyledContent>
          <VisualContainer>
            <StatusChip $status={referral.status}>{referral.statusLabel}</StatusChip>
            <RightArrow />
            <StatusChip $status={proposedStatusMap[referral.status].status}>
              {proposedStatusMap[referral.status].label}
            </StatusChip>
          </VisualContainer>
        </StyledContent>
        <StyledSubHeader>
          <b>
            You are about to change the referral status from {referral.statusLabel} to{' '}
            {proposedStatusMap[referral.status].label}.
          </b>{' '}
          Updating the referral status will notify the co-management team.
        </StyledSubHeader>
        <InputGroup>
          {referral.accepted && (
            <InputContainer>
              <label>Denial Reason</label>
              <Select
                options={referral.denialReasonOptions}
                onChange={(val) => setDenialReason(val)}
                getOptionValue={(prop) => prop.apiName}
                getOptionLabel={(prop) => prop.displayName}
                value={denialReason}
                placeholder='Select a denial reason'
              />
            </InputContainer>
          )}
          <InputContainer>
            <label>Referral Notes (optional)</label>
            <ActivityInput
              taggableUserProps={{ onReview: referral.rejected ? referral?.id : undefined }}
              locationEpisodeId={locationEpisode.id}
              values={note}
              setValues={setNote}
              onUploading={setAttachmentUploading}
            />
          </InputContainer>
        </InputGroup>
      </StyledContainer>
      <Actions>
        <Button variant='ghost' onClick={() => setShow(false)}>
          Cancel
        </Button>
        <Button disabled={submitDisabled} onClick={handleSubmit}>
          Submit
        </Button>
      </Actions>
    </StandardModal>
  );
};

export default StatusUpdateModal;

const StyledContainer = styled.div`
  color: ${colors.black};
`;

const StyledContent = styled(StandardModal.ModalContent)`
  margin: 32px 0;
`;

const StyledSubHeader = styled(StandardModal.SubHeader)`
  margin-bottom: 24px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  label {
    color: ${colors.black75};
    font-size: 12px;
    margin-bottom: 4px;
  }
`;

const VisualContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 36px;
`;

const { PatientName, Actions } = StandardModal;
